import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {CompanyType, toCompanyType} from "./company";
import {GeoCoord} from "./geo-coord";
import {Entity, Locatable} from "./entity";
import {TrailerStateEntity, TrailerStateValidator} from "./trailer-state";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {validateJSON, validateJSONNow} from "../web/validation";

/** Validator for trailer types. */
export const TrailerValidator = t.intersection([
  t.type({
    id: t.string,
    externalId: t.string,
    companyId: t.string,
    sizeDecimal: t.number,
    maxSize: t.number,
    sizeUnitOfMeasure: t.string,
    weight: t.number,
    weightUnitOfMeasure: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    barCode: t.string,
    regionList: t.array(t.string),
    waterTypeId: t.string,
    waterTypeName: t.string,
    companyName: t.string,
    companyType: t.string,
    state: TrailerStateValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    database: t.string
  })
]);

/** Validator for an array of trailer types. */
export const TrailerArrayValidator = t.array(TrailerValidator);

export type Trailer = t.TypeOf<typeof TrailerValidator>;
export type TrailerArray = t.TypeOf<typeof TrailerArrayValidator>;

/**
 * A trailer.
 */
@Exclude()
export class TrailerEntity implements Entity, Locatable {
  @Expose()
  id!: string;

  @Expose({name: "externalId"})
  name!: string;

  @Expose({name: "barCode"})
  barcode?: string;

  @Expose({name: "sizeDecimal"})
  size!: number;

  @Expose()
  maxSize!: number;

  @Expose()
  sizeUnitOfMeasure!: string;

  @Expose()
  weight!: number;

  @Expose()
  weightUnitOfMeasure!: string;

  @Expose()
  companyId!: string;

  @Expose()
  companyName?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCompanyType))
  companyType?: CompanyType;

  @Expose({name: "waterTypeId"})
  freightTypeId?: string

  @Expose({name: "waterTypeName"})
  freightTypeName?: string

  @Expose()
  @Type(() => TrailerStateEntity)
  state?: TrailerStateEntity;

  @Expose()
  doNotUse = false;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose({name: "regionList"})
  regionIds?: string[];

  @Expose()
  readonly database?: string;

  readonly entityType = "TRAILER";

  get displayName(): Readonly<string> {
    let name = this.name;
    if (this.companyName) {
      name += ` (${this.companyName})`;
    }
    return name;
  }

  get latLng(): Readonly<GeoCoord | undefined> {
    return this.state?.location;
  }

  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(trailer: Trailer): TrailerEntity {
    return plainToInstance(TrailerEntity, trailer, {
      excludeExtraneousValues: true
    });
  }

  loadTypeValue(): Promise<Trailer> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(TrailerValidator, object);
  }

  asTypeValue(): Trailer {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(TrailerValidator, object);
  }
}
